import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const referralSources = [
  { value: 'google_search', label: 'Google' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'referral', label: 'Empfehlung Freund/Kollege' },
  { value: 'blog', label: 'Artikel / Blog' },
  { value: 'email', label: 'E-Mail' },
  { value: 'whatsapp', label: 'WhatsApp / Chat' },
  { value: 'paid_ad', label: 'Werbung' },
  { value: 'other', label: 'Sonstiges' }
];

const PostPaymentSurvey = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [referralSource, setReferralSource] = useState('');
  const [referralSourceOther, setReferralSourceOther] = useState('');
  const [loading, setLoading] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [showConfetti, setShowConfetti] = useState(true);
  const sessionId = searchParams.get('session_id');

  // Update window dimensions for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Stop confetti after 5 seconds
  useEffect(() => {
    console.log('🎉 Confetti started!', { showConfetti, windowDimensions });
    const timer = setTimeout(() => {
      console.log('🎉 Confetti stopped after 5 seconds');
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // If no session_id, redirect to dashboard
    if (!sessionId) {
      navigate('/dashboard');
    }
  }, [sessionId, navigate]);

  const handleSubmit = async () => {
    if (!referralSource) {
      toast.error('Bitte wähle eine Option aus');
      return;
    }

    if (referralSource === 'other' && !referralSourceOther.trim()) {
      toast.error('Bitte gib an, wo du von uns gehört hast');
      return;
    }

    await saveReferralSource(false);
  };

  const handleSkip = async () => {
    await saveReferralSource(true);
  };

  const saveReferralSource = async (skipped) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/payment/save-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          referral_source: skipped ? null : referralSource,
          referral_source_other: skipped ? null : referralSourceOther,
          referral_skipped: skipped
        })
      });

      if (!response.ok) throw new Error('Fehler beim Speichern');

      toast.success(skipped ? 'Weiterleitung zum Dashboard...' : 'Vielen Dank für dein Feedback!');
      
      // Redirect to dashboard after short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Speichern. Du wirst trotzdem weitergeleitet...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Confetti Effect */}
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          numberOfPieces={200}
          recycle={false}
          gravity={0.2}
          colors={['#0040FF', '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE', '#E5E7EB']}
          opacity={0.8}
          wind={0.02}
          style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999, pointerEvents: 'none' }}
        />
      )}

      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center py-8 px-4">
        <div className="max-w-2xl w-full">
          {/* Top Header with Logo */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="AdSimplify Logo"
                className="h-10 w-10 object-contain"
              />
              <span className="text-2xl font-bold text-gray-900">AdSimplify</span>
            </div>
          </div>

          <Card className="shadow-sm">
            <CardContent className="p-8 md:p-12">
              {/* Success Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-600 stroke-[3]" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
                Vielen Dank!
              </h1>
              
              <p className="text-center text-gray-600 mb-8 text-lg">
                Zum Abschluss noch eine kurze Frage:
              </p>

              {/* Survey Question */}
              <div className="bg-[#E6EDFF] rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-2 text-center text-gray-900">
                  Wie hast du von AdSimplify erfahren?
                </h2>
                
                <p className="text-sm text-gray-600 text-center mb-6">
                  Das hilft uns, unsere Kanäle gezielt weiterzuentwickeln.
                </p>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {referralSources.map((source) => (
                    <button
                      key={source.value}
                      type="button"
                      onClick={() => {
                        setReferralSource(source.value);
                        if (source.value !== 'other') {
                          setReferralSourceOther('');
                        }
                      }}
                      disabled={loading}
                      className={`px-4 py-3 rounded-lg font-medium text-sm transition-all border-2 ${
                        referralSource === source.value
                          ? 'bg-[#0040FF] text-white border-[#0040FF] shadow-md'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-[#0040FF]'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {source.label}
                    </button>
                  ))}
                </div>

                {/* Show text input when "Sonstiges" is selected */}
                {referralSource === 'other' && (
                  <div className="mt-3">
                    <Input
                      type="text"
                      value={referralSourceOther}
                      onChange={(e) => setReferralSourceOther(e.target.value)}
                      disabled={loading}
                      placeholder="Bitte angeben..."
                      maxLength={100}
                      className="h-11"
                    />
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !referralSource}
                  className="w-full h-12 bg-[#0040FF] hover:bg-[#0033CC] text-white font-medium text-base"
                >
                  {loading ? 'Wird gespeichert...' : (
                    <>
                      Weiter zum Dashboard
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={loading}
                  className="w-full h-11 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                >
                  Später
                </Button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-6">
                Diese Frage hilft uns, AdSimplify für dich zu verbessern.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default PostPaymentSurvey;
